import { memo, type FC, useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue, useSetRecoilState } from "recoil"
import type { FlattenSimpleInterpolation } from "styled-components"
import styled, { css } from "styled-components"

import useCurrentModuleEquipment from "~/api/hooks/useCurrentModuleEquipment"
import { Dropdown } from "~/components/ui"
import {
  isSelectedModuleSavedState,
  selectedModuleRemoteState,
  selectedModuleState,
} from "~/containers/LegacyDesigner/state/elements/panels"
import type { DEFAULT_SELECTED_MODULE } from "~/containers/LegacyDesigner/state/elements/panels"
import { SERVICE_BUREAU_PHASE_ALLOWED_PERMISSIONS } from "~/containers/LegacyDesigner/state/phase"
import type { IPart } from "~/containers/Proposal/Setup/Form/setup-steps-types"
import { useDesignContext } from "~/containers/Proposal/state/designs/design-context"
import { getIncompatibleSelectedInverters } from "~/containers/Proposal/state/equipment/module-inverter-compatibility"
import { useBaseEquipment } from "~/containers/Proposal/state/equipment/use-base-equipment"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { syncWaitingForEntityState } from "~/containers/Proposal/state/sync-state"
import {
  userHasRequiredPermissionsState,
  currentUserIsDesignRequestUserState,
} from "~/containers/Proposal/state/user/user-state"
import type { UpdateModuleMutation } from "~/graphql/client"
import {
  NewEquipmentType,
  useGetEquipmentForProposalQuery,
  useUpdateModuleMutation,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { formatCurrency } from "~/utils/finance/format"
import { isPowurLineProject } from "~/utils/powurline-utils"

import { ModuleInverterCompatibilityWarning } from "./ModuleInverterCompatibilityWarning"
import { SingleSelectForm } from "./StorageSelection"
import { SetupMenuFormFooter } from "../SetupMenuFooter"

const ModuleSelect_Internal = ({
  isDisabled,
  onSelectionChange,
  onSelectionSave,
  wrapperStyle,
}: {
  isDisabled?: boolean
  onSelectionChange?: (selectedModule: typeof DEFAULT_SELECTED_MODULE) => void
  onSelectionSave?: (
    selectedModule: typeof DEFAULT_SELECTED_MODULE,
  ) => Promise<UpdateModuleMutation | null>
  wrapperStyle?: FlattenSimpleInterpolation
}) => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const proposal = useProposalContext()
  const proposalId = proposal.local.id
  const setLocalModule = useSetRecoilState(selectedModuleState)
  const localModule = useRecoilValue(selectedModuleState)
  const currentEquipmentQuery = useCurrentModuleEquipment(proposalId, {
    onError,
  })
  const solarModulesQuery = useGetEquipmentForProposalQuery(
    client,
    {
      proposalId,
      equipmentType: NewEquipmentType.MODULE,
      inverterManufacturerId: null,
    },
    {
      onError,
      enabled: Boolean(proposalId),
    },
  )

  const { t } = useTranslation()

  const { isBaseModule } = useBaseEquipment()
  const modules = useMemo(
    () => solarModulesQuery.data?.equipmentForProposal ?? [],
    [solarModulesQuery.data?.equipmentForProposal],
  )
  const isPowurLine = isPowurLineProject(proposal.remote.enterpriseSettings)
  const { refetch } = solarModulesQuery
  const currentModulesById = useMemo(
    () =>
      new Map(
        currentEquipmentQuery.data?.modules.map((module) => [module.id, module]),
      ),
    [currentEquipmentQuery.data?.modules],
  )
  const selectableModules = useMemo(
    () => modules.filter(({ id }) => currentModulesById.has(id)),
    [currentModulesById, modules],
  )

  // Refetch equipment when component mounts
  useEffect(() => {
    if (!proposalId) {
      return
    }

    refetch()
  }, [proposalId, refetch])

  return (
    <Dropdown.Container
      SelectedOption={() => (
        <>
          <p>{localModule?.name ?? `None`}</p>
          <span style={{ display: `none` }}>
            {createModulePricePerWatt(localModule)}/W
          </span>
        </>
      )}
      wrapperStyle={wrapperStyle}
      disabled={isDisabled || !currentEquipmentQuery.isSuccess}
      Label={() => <label>{t(`module`)}</label>}
      Options={selectableModules.map((item) => {
        const isBase = isBaseModule(item.id)
        const currentModuleData =
          currentModulesById.get(item.id)?.moduleData ?? null

        return (
          <Dropdown.Item
            label1={createModuleName(item, isPowurLine)}
            key={item.id}
            label2={`${createModulePricePerWatt(item)}/W`}
            isBase={isBase}
            onClick={() => {
              const selectedModule = {
                currentProposalPrice: item.currentProposalPrice,
                height: item.height ?? 0,
                id: item.id,
                isDefault: item.isDefault,
                manufacturer: item.manufacturer,
                moduleData: currentModuleData,
                name: item.name,
                visionId: `set from local`,
                wattage: item.wattage,
                width: item.width ?? 0,
              }

              setLocalModule(selectedModule)
              onSelectionChange?.(selectedModule)

              if (item.id !== localModule?.id) {
                void onSelectionSave?.(selectedModule)
              }
            }}
            isChecked={item.id === localModule?.id}
          />
        )
      })}
    />
  )
}

const AutoSavingModuleSelect = (
  props: Omit<Parameters<typeof ModuleSelect_Internal>[0], `onSelectionSave`>,
) => {
  const updateModule = useSaveModule()

  return <ModuleSelect_Internal {...props} onSelectionSave={updateModule.save} />
}

export const StagedModuleSelect = memo(ModuleSelect_Internal)
export const ModuleSelect = memo(AutoSavingModuleSelect)

export const ModuleSelectionSetupForm: IPart[`form`] = () => {
  const onError = useDispatchError()
  const proposal = useProposalContext()
  const updateModule = useSaveModule()
  const isSaved = useRecoilValue(isSelectedModuleSavedState)
  const selectedModule = useRecoilValue(selectedModuleState)
  const design = useDesignContext()
  const modulesQuery = useCurrentModuleEquipment(proposal.local.id, { onError })
  const currentSelectedModule = useMemo(
    () => modulesQuery.data?.modules.find(({ id }) => id === selectedModule.id),
    [modulesQuery.data, selectedModule.id],
  )
  const isCompatibilityReady = modulesQuery.isSuccess
  const selectedModuleData = currentSelectedModule?.moduleData
  const currentInverterIds = useMemo(
    () => new Set(modulesQuery.data?.inverters.map(({ id }) => id)),
    [modulesQuery.data?.inverters],
  )
  const incompatibleSelectedInverters = useMemo(
    () =>
      getIncompatibleSelectedInverters(
        selectedModuleData,
        [design.local.inverter, design.local.secondaryInverter],
        currentInverterIds,
      ),
    [
      currentInverterIds,
      design.local.inverter,
      design.local.secondaryInverter,
      selectedModuleData,
    ],
  )
  const hasIncompatibleSelection = incompatibleSelectedInverters.length > 0

  return (
    <SingleSelectForm>
      <section>
        <Container>
          <ModuleSelect_Internal isDisabled={!isCompatibilityReady} />
        </Container>
      </section>
      {hasIncompatibleSelection && (
        <ModuleInverterCompatibilityWarning
          moduleName={currentSelectedModule?.name ?? selectedModule.name}
          inverterNames={incompatibleSelectedInverters.map(({ name }) => name)}
        />
      )}
      <SetupMenuFormFooter
        isLoading={modulesQuery.isLoading || updateModule.mutation.isLoading}
        isDirty={isCompatibilityReady && !isSaved && !hasIncompatibleSelection}
        isDisabled={!isCompatibilityReady || hasIncompatibleSelection}
        onSubmit={async () => {
          if (!isCompatibilityReady || isSaved || hasIncompatibleSelection) {
            return
          }

          await updateModule.save(selectedModule)
        }}
      />
    </SingleSelectForm>
  )
}

export const ModuleSelectionDropdownForDesigner_OS: FC = () => {
  const hasServiceBureauRequiredPermissions = useRecoilValue(
    userHasRequiredPermissionsState(SERVICE_BUREAU_PHASE_ALLOWED_PERMISSIONS),
  )
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )

  const isDisabled =
    !hasServiceBureauRequiredPermissions || !currentUserIsDesignUser

  return <ModuleSelect isDisabled={isDisabled} wrapperStyle={wrapperStyle} />
}

export const useSaveModule = (): {
  mutation: ReturnType<typeof useUpdateModuleMutation>
  save: (
    selectedModule?: typeof DEFAULT_SELECTED_MODULE,
  ) => Promise<UpdateModuleMutation | null>
} => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const design = useDesignContext()
  const moduleLocal = useRecoilValue(selectedModuleState)
  const setModuleRemote = useSetRecoilState(selectedModuleRemoteState)
  const updateModule = useUpdateModuleMutation(client, {
    onError,
  })
  const setSyncDesign = useSetRecoilState(syncWaitingForEntityState(`design`))

  return {
    mutation: updateModule,
    save: async (selectedModule = moduleLocal) => {
      if (!Number.isFinite(selectedModule.id)) {
        console.warn(`No module selected`)

        return null
      }

      const updated = await updateModule.mutateAsync({
        designId: design.remote.id,
        equipmentId: selectedModule.id,
      })

      setModuleRemote(selectedModule)
      setSyncDesign(true)

      return updated
    },
  }
}

const createModulePricePerWatt = (item: SelectedModule | null) => {
  if (!item?.wattage) {
    return `-`
  }

  return formatCurrency((item.currentProposalPrice ?? 0) / item.wattage)
}

const createModuleName = (item: SelectedModule, isPowurLine = false) => {
  if (!item) {
    return `None`
  }
  if (!isPowurLine && item.isDefault) {
    return item.name + ` (Recommended)`
  }

  return item.name
}

type SelectedModule = {
  wattage: number
  id: number
  name: string
  currentProposalPrice: number
  isDefault: boolean
  moduleData?: { incompatibleInverters?: number[] | null } | null
}

const Container = styled.div`
  cursor: pointer;
  > p {
    cursor: initial;
    margin-bottom: 5px;
  }
`

const wrapperStyle = css`
  flex-direction: row;
  align-items: center;
  max-width: 350px;
  min-width: fit-content;
  [data-role="dropdown-wrapper"] {
    height: 40px;
  }
  label {
    text-transform: capitalize;
  }
`
